'use client';

import { useState } from 'react';
import Image from 'next/image';

import { GroqImage } from '@/helpers/Image';
import type { PageRizAffiliateSchema } from '@/sanity/pages/PageRizAffiliateTemplate';
import { measuredRichText, V3_HIGHLIGHT } from './richtext';
import { RevealBlock, RevealEyebrow, RevealRichText, RevealStyles } from './about/reveal';

import iqColumn from '@/public/riz-affiliate/why-iq-column.png';
import statue from '@/public/riz-affiliate/why-statue.png';
import logoAlt from '@/public/fabio-affiliate/logo-alt.svg';

const HEADING_CLASS = `${V3_HIGHLIGHT} richtext richtext-center w-[min(37.25rem,100%)] lg:w-[min(50rem,100%)] prose-headings:text-black prose-headings:font-geist prose-headings:font-[500] prose-headings:text-[4.5rem] prose-headings:tracking-[-0.225rem] lg:prose-headings:text-[6rem] lg:prose-headings:tracking-[-0.3rem] [--hl-tracking:-0.03em] [--hl-wash:4.5rem] lg:[--hl-wash:6.75rem] [--hl-dy:0.125rem] lg:[--hl-dy:0.25rem] max-lg:[--hl-ctr:0px] [--hl-pl:0.125rem] lg:[--hl-pl:0.55rem] [--hl-pr:0.25em] lg:[--hl-pr:0.2em] [&_span]:!text-black prose-headings:leading-none lg:prose-headings:leading-[1.15]`;

const TITLE_CLASS = 'font-geist font-[600] text-black text-[3rem] leading-[1.12]';

export default function ComparisonSection({
  data,
}: {
  data: PageRizAffiliateSchema;
}) {
  const [selectedTab, setSelectedTab] = useState<'iq' | 'other'>('iq');

  if (
    !data?.comparisonChip &&
    !data?.comparisonHeading?.length &&
    !data?.comparisonColumns?.length &&
    !data?.comparisonRows?.length
  )
    return null;

  const columns = data?.comparisonColumns ?? [];
  const rows = data?.comparisonRows ?? [];
  const [featuresColumn, iqColumnData, otherColumn] = columns;

  const columnTitle = (column: (typeof columns)[number] | undefined, logoWidth: string) =>
    column?.logo ? (
      <GroqImage
        src={column.logo}
        alt={column.label ?? ''}
        style={{ width: logoWidth }}
        className="h-auto"
      />
    ) : (
      column?.label && <span className={TITLE_CLASS}>{column.label}</span>
    );

  const tabs = [
    { type: 'iq' as const, column: iqColumnData },
    { type: 'other' as const, column: otherColumn },
  ];

  return (
    <section className="bg-white px-[2rem] py-[8rem] lg:px-0 lg:pt-[15rem] lg:pb-[22.5rem]">
      <RevealStyles />
      <div className="mx-auto w-[min(140rem,100%)]">
        <div className="flex flex-col items-center text-center mb-[4.5rem] gap-[2rem] lg:mb-[4.325rem] lg:gap-[1.25rem]">
          <RevealEyebrow text={data?.comparisonChip} />
          <RevealRichText
            value={data?.comparisonHeading}
            components={measuredRichText}
            className={HEADING_CLASS}
          />
        </div>

        {(columns.length > 0 || rows.length > 0) && (
          <>
            <RevealBlock className="hidden relative lg:block h-[52.75rem]">
              <Image
                src={iqColumn}
                alt=""
                sizes="19vw"
                className="absolute top-0 left-[44.625rem] w-[45.5rem] h-full rounded-[0.75rem] object-cover pointer-events-none"
              />
              <div
                aria-hidden
                className="absolute top-0 left-[92.25rem] w-[45.5rem] h-full rounded-[0.75rem] bg-[#112E27]/[0.08]"
              />
              <Image
                src={statue}
                alt=""
                sizes="7.3vw"
                className="absolute top-[1.3125rem] left-[69.375rem] w-[17.375rem] h-auto pointer-events-none"
              />

              <div className="grid relative grid-cols-3 gap-x-[3rem] pt-[5.5625rem] h-[17.3125rem] items-start">
                <div className="flex items-center">{columnTitle(featuresColumn, '15.75rem')}</div>
                <div className="flex items-center pl-[0.5rem] [&_span]:text-white">{columnTitle(iqColumnData, '15.75rem')}</div>
                <div className="flex items-center">{columnTitle(otherColumn, '15.75rem')}</div>
              </div>

              <div className="relative">
                {rows.map((row, i) => (
                  <div
                    key={`row-${i}`}
                    className="grid relative grid-cols-3 gap-x-[3rem] items-start h-[6.5rem] font-geist leading-[1.12]"
                  >
                    <p className="m-0 uppercase font-[500] text-[2rem] text-black/50">{row?.feature ?? ''}</p>
                    <p className="m-0 font-[400] text-[2.25rem] text-white">{row?.values?.[0]?.text ?? ''}</p>
                    <p className="m-0 font-[400] text-[2.25rem] text-black">{row?.values?.[1]?.text ?? ''}</p>
                    {i < rows.length - 1 && (
                      <span
                        aria-hidden
                        className="absolute inset-x-0 top-[4.5rem] h-px bg-white/[0.15] mix-blend-exclusion"
                      />
                    )}
                  </div>
                ))}
              </div>
            </RevealBlock>

            <RevealBlock className="flex flex-col lg:hidden gap-[1rem]">
              <div className="flex bg-black/[3%] border border-black/5 rounded-[0.75rem] gap-[0.5rem] p-[0.5rem]">
                {tabs.map((tab) => {
                  const isActive = tab.type === selectedTab;
                  return (
                    <button
                      key={tab.type}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => setSelectedTab(tab.type)}
                      className={`riz-tab flex flex-1 justify-center items-center rounded-[0.5rem] px-[2rem] py-[1.875rem] transition-colors ${
                        isActive ? 'bg-[#003828] text-white' : 'text-black/40 opacity-50'
                      }`}
                    >
                      {tab.column?.logo ? (
                        isActive ? (
                          <GroqImage
                            src={tab.column.logo}
                            alt={tab.column.label ?? ''}
                            style={{ width: '14.25rem' }}
                            className="h-auto"
                          />
                        ) : (
                          <Image src={logoAlt} alt={tab.column.label ?? ''} className="pointer-events-none w-[14.25rem]" />
                        )
                      ) : (
                        tab.column?.label && (
                          <span className="font-geist text-[2.5rem] font-[600] leading-[1.12] tracking-[-0.05rem]">
                            {tab.column.label}
                          </span>
                        )
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-col border rounded-[0.375rem] bg-[#228E71]/5 border-black/5 gap-[2.75rem] px-[2rem] py-[1.75rem]">
                {featuresColumn?.label && <p className={`m-0 ${TITLE_CLASS}`}>{featuresColumn.label}</p>}
                <div className="flex flex-col gap-[3.25rem]">
                  {rows.map((row, i) => {
                    const value = row?.values?.[selectedTab === 'iq' ? 0 : 1]?.text;
                    return (
                      <div
                        key={`m-row-${i}`}
                        className="flex items-center font-geist font-[500] text-[2rem] leading-[1.12] gap-[1.25rem]"
                      >
                        <span className="uppercase text-black/50 shrink-0">{row?.feature ?? ''}</span>
                        <span
                          aria-hidden
                          className="flex-1 h-px min-w-[1rem] bg-[linear-gradient(to_right,rgba(0,0,0,0.1)_50%,transparent_50%)] bg-[length:0.49rem_1px]"
                        />
                        <span className="text-right text-black shrink-0">{value ?? ''}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </RevealBlock>
          </>
        )}
      </div>
      <style jsx>{`
        .riz-tab:focus-visible {
          outline: 2px solid #9fe0b0;
          outline-offset: 2px;
        }
      `}</style>
    </section>
  );
}
